import {
  LayoutDashboard,
  Wand2,
  Globe,
  ShieldCheck,
  Webhook,
  Bot,
  Cpu,
  Siren,
  BarChart3,
  Receipt,
  ScrollText,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import type { WaapPlanFeatures } from '@/api/waap';

/** A nested nav child rendered inside a collapsible parent group. */
export interface WaapNavChild {
  label: string;
  /** Path segment under `/services/waap/:serviceId` (e.g. 'aiwaf/general'). */
  slug: string;
}

export interface WaapNavEntry {
  label: string;
  /** Path segment under `/services/waap/:serviceId` ('' = console root/Dashboard). */
  slug: string;
  icon: LucideIcon;
  /** When present, the entry is a collapsible parent; children are the leaves. */
  children?: WaapNavChild[];
  /** Plan-gated feature: the entry is hidden unless the active subscription plan
   *  includes it. Absent = always shown. */
  feature?: keyof WaapPlanFeatures;
}

/**
 * WAAP console navigation — mirrors the SwiftFederation portal's left nav so the
 * partner UI maps 1:1. Slugs map to the nested routes in WaapConsole. Rendered in
 * the app's scope-driven ContextualSidebar (same mechanism as the CDN console).
 */
export const WAAP_NAV: WaapNavEntry[] = [
  { label: 'Dashboard', slug: '', icon: LayoutDashboard },
  { label: 'Onboarding', slug: 'onboarding', icon: Wand2 },
  { label: 'Websites', slug: 'websites', icon: Globe },
  { label: 'Analytics', slug: 'analytics', icon: BarChart3 },
  {
    label: 'AI-WAF',
    slug: 'aiwaf/general',
    icon: ShieldCheck,
    children: [
      { label: 'General Protection', slug: 'aiwaf/general' },
      { label: 'Advanced Protection', slug: 'aiwaf/advanced' },
      { label: 'Compliance Validation', slug: 'aiwaf/compliance' },
      { label: 'Custom Rules', slug: 'aiwaf/custom-rules' },
      { label: 'Web Access Control', slug: 'aiwaf/web-access-control' },
      { label: 'Settings', slug: 'aiwaf/settings' },
    ],
  },
  {
    label: 'API Protection',
    slug: 'api-protection',
    icon: Webhook,
    feature: 'apiProtection',
    children: [
      { label: 'Management', slug: 'api-protection' },
      { label: 'Discovery', slug: 'api-protection/discovery' },
      { label: 'Threat Detection', slug: 'api-protection/threat-detection' },
      { label: 'Data Loss Prevention', slug: 'api-protection/data-loss' },
      { label: 'Vulnerability Detection', slug: 'api-protection/vulnerability' },
    ],
  },
  { label: 'Bot Management', slug: 'bot-management', icon: Bot, feature: 'botManagement' },
  {
    label: 'Programmable Mitigation',
    slug: 'programmable-mitigation',
    icon: Cpu,
    feature: 'programmableMitigation',
  },
  { label: 'Emergency Mitigation', slug: 'emergency-mitigation', icon: Siren },
  { label: 'Business Usage', slug: 'usage', icon: Receipt },
  { label: 'Logs', slug: 'logs', icon: ScrollText },
  { label: 'Settings', slug: 'settings', icon: Settings },
];

/** Absolute href for a slug under the console base ('' slug = base/root). */
export function waapHref(base: string, slug: string): string {
  return slug ? `${base}/${slug}` : base;
}

/** Every selectable href under the console (parents flattened to their children). */
function allHrefs(base: string): string[] {
  return WAAP_NAV.flatMap((e) =>
    e.children ? e.children.map((c) => waapHref(base, c.slug)) : [waapHref(base, e.slug)],
  );
}

/**
 * The active entry href for the current pathname (longest-prefix match). `base`
 * and `base/dashboard` both resolve to the Dashboard entry ('' slug).
 */
export function waapActiveId(base: string, pathname: string): string {
  const normalized = pathname === `${base}/dashboard` ? base : pathname;
  const matches = allHrefs(base)
    .filter((href) => normalized === href || normalized.startsWith(`${href}/`))
    .sort((a, b) => b.length - a.length);
  return matches[0] ?? base;
}
